import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LifeBuoy, Send, Loader2, CheckCircle2, MessageSquare, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const CATEGORIES = [
  { key: "general", label: "General question" },
  { key: "bug",     label: "Something is broken" },
  { key: "billing", label: "Billing / payment" },
  { key: "account", label: "Account / sign-in" },
  { key: "feature", label: "Feature request" },
  { key: "other",   label: "Other" },
];

const PRIORITIES = [
  { key: "low",    label: "Low" },
  { key: "normal", label: "Normal" },
  { key: "high",   label: "High" },
  { key: "urgent", label: "Urgent" },
];

const STATUS_PILL = {
  open:           { label: "Open",          cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  in_progress:    { label: "In progress",   cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  waiting_user:   { label: "Awaiting you",  cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40" },
  resolved:       { label: "Resolved",      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  closed:         { label: "Closed",        cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/40" },
};

export default function SupportPage() {
  const [form, setForm] = useState({
    subject: "", body: "", category: "general", priority: "normal",
    name: "", email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [myTickets, setMyTickets] = useState([]);
  const [authed, setAuthed] = useState(false);

  // Try to fetch the user's existing tickets if signed in.
  useEffect(() => {
    const tok = localStorage.getItem("auth_token");
    if (!tok || tok === "pending") return;
    setAuthed(true);
    api.get("/support/tickets/me").then((r) => setMyTickets(r.data || [])).catch(() => {});
  }, [createdId]);

  const set = (patch) => setForm((s) => ({ ...s, ...patch }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.subject.trim() || !form.body.trim()) { toast.error("Subject and message are required"); return; }
    if (!authed && !form.email.trim()) { toast.error("Please add an email so we can reply"); return; }
    setSubmitting(true);
    try {
      const payload = {
        subject: form.subject, body: form.body,
        category: form.category, priority: form.priority,
        page_url: typeof window !== "undefined" ? window.location.href : "",
      };
      if (!authed) {
        payload.name = form.name; payload.email = form.email;
      }
      const r = await api.post("/support/tickets", payload);
      setCreatedId(r.data?.id || true);
      setForm((s) => ({ ...s, subject: "", body: "" }));
      toast.success("Ticket submitted — we typically reply within 24 hours.");
    } catch (err) {
      toast.error(errorMessage(err, "Could not submit ticket"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16 px-4 sm:px-6 lg:px-8" data-testid="support-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <LifeBuoy className="w-7 h-7 text-gold" />
          <h1 className="text-3xl sm:text-4xl text-gold font-display">24-hour Support</h1>
        </div>
        <p className="text-sm text-white/60 mb-8">
          Anything wrong, confusing, or just a question — drop it here. A support agent typically replies within 24 hours, often much faster.
        </p>

        <motion.form
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className="bg-zinc-950/60 border border-gold/20 rounded-md p-5 space-y-3"
        >
          {!authed && (
            <div className="grid grid-cols-2 gap-3">
              <input
                data-testid="support-name"
                type="text" value={form.name} onChange={(e) => set({ name: e.target.value })}
                placeholder="Your name"
                className="bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white"
              />
              <input
                data-testid="support-email"
                type="email" value={form.email} onChange={(e) => set({ email: e.target.value })}
                placeholder="your@email.com"
                className="bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <select
              data-testid="support-category"
              value={form.category} onChange={(e) => set({ category: e.target.value })}
              className="bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white"
            >
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <select
              data-testid="support-priority"
              value={form.priority} onChange={(e) => set({ priority: e.target.value })}
              className="bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white"
            >
              {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <input
            data-testid="support-subject"
            type="text" value={form.subject} onChange={(e) => set({ subject: e.target.value })}
            placeholder="Short summary (e.g. Can't book a ride to Half Way Tree)"
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white"
          />
          <textarea
            data-testid="support-body"
            value={form.body} onChange={(e) => set({ body: e.target.value })}
            placeholder="Tell us what happened — include any error message, screenshot URL, or page you were on."
            rows={6}
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white"
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-white/45 inline-flex items-center gap-1">
              <Clock size={12} /> Average reply: under 24 hours
            </p>
            <button
              data-testid="support-submit"
              type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 bg-gold text-black hover:bg-gold/90 rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit ticket
            </button>
          </div>
          {createdId && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" />
              <div className="text-xs text-emerald-100/85">
                Ticket received. {authed ? "Track its status below." : "We'll reply to your email."}
              </div>
            </div>
          )}
        </motion.form>

        {authed && myTickets.length > 0 && (
          <div className="mt-10">
            <h2 className="text-lg text-gold font-display mb-3 inline-flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> My tickets
            </h2>
            <div className="space-y-2">
              {myTickets.map((t) => {
                const s = STATUS_PILL[t.status] || STATUS_PILL.open;
                return (
                  <a key={t.id} href={`/support/${t.id}`} data-testid={`support-ticket-${t.id}`}
                    className="block rounded-md border border-white/10 hover:border-gold/30 bg-black/30 p-3 transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm text-white truncate">{t.subject}</div>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${s.cls}`}>
                        {s.label}
                      </span>
                    </div>
                    <div className="text-[11px] text-white/40 mt-0.5">
                      {t.category} · updated {new Date(t.updated_at).toLocaleString()}
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
